import $api from '../https';
import { AxiosResponse } from 'axios';
import Token from '../model/Token';
import SignIn from '../model/SignIn';
import { Register } from '../model/form/Register';
import { Counter } from '../model/Counter';
import { Attachment } from '../model/Attachment';
import { Category } from '../model/Category';
import { RejectionReason } from '../model/RejectionReason';
import { Municipality } from '../model/Municipality';
import { Appeal } from '../model/Appeal';
import { MunicipalityExecutors } from '../model/MunicipalityExecutors';
import { AppealAnswer } from '../model/AppealAnswer';
import { CitizenMessage } from '../model/CitizenMessage';
import { AppealPlacemark } from '../model/AppealPlacemark';
import { Page } from '../model/Page';
import { User } from '../model/User';
import { AppealForm } from '../model/form/AppealForm';
import { AppealStatus } from '../model/AppealStatus';
import { AppealCoordination } from '../model/AppealCoordination';
import { AppealApproval } from '../model/AppealApproval';
import { ExecutorAnswer } from '../model/ExecutorAnswer';
import { Status } from '../model/Status';
import { AppealAccept } from '../model/AppealAccept';
import {
  ChangePassword,
  Restore,
  RestoreError,
  RestoreSuccess,
} from '../model/form/Restore';

export default class AppService {
  static async login(data: SignIn): Promise<AxiosResponse<Token>> {
    return $api.post<Token>('auth', data);
  }

  static async register(data: Register): Promise<AxiosResponse<Register>> {
    return $api.post<Register>('/user/register', data);
  }

  static async restorePassword(
    data: Restore
  ): Promise<AxiosResponse<RestoreSuccess | RestoreError>> {
    const requestParams = new URLSearchParams();
    if (data !== undefined) {
      requestParams.append('email', String(data.email));
    }
    return $api.get<RestoreSuccess | RestoreError>(
      `/user/restore_password` + (requestParams ? `?${requestParams}` : ``)
    );
  }

  static async checkRestoreHash(
    hash: string
  ): Promise<AxiosResponse<RestoreSuccess | RestoreError>> {
    const requestParams = new URLSearchParams();
    requestParams.append('restoreHash', hash);

    return $api.get<RestoreSuccess | RestoreError>(
      `/user/check_hash?${requestParams}`
    );
  }

  static async changePassword(
    changePassword: ChangePassword
  ): Promise<AxiosResponse<RestoreSuccess | RestoreError>> {
    return $api.put<RestoreSuccess | RestoreError>(
      `/user/change_password`,
      changePassword
    );
  }

  static async getCounters(): Promise<AxiosResponse<Counter>> {
    return $api.get<Counter>('appeals/counters');
  }

  static async upload(formData: FormData): Promise<AxiosResponse<Attachment>> {
    return $api.post<Attachment>('/attachments', formData);
  }

  static async deleteFile(id: number): Promise<AxiosResponse<Attachment>> {
    return $api.delete<Attachment>(`/attachments/${id}`);
  }

  static async getAppealCategories(): Promise<AxiosResponse<Category[]>> {
    return $api.get<Category[]>('/appeal_categories');
  }

  static async getRejectionReasons(): Promise<
    AxiosResponse<RejectionReason[]>
  > {
    return $api.get<RejectionReason[]>('/rejection_reasons');
  }

  static async getMunicipalities(
    municipalityName?: string
  ): Promise<AxiosResponse<Municipality[]>> {
    return $api.get<Municipality[]>(
      '/municipalities' + (municipalityName ? '?name=' + municipalityName : '')
    );
  }

  static async getAppealsList(
    author?: number
  ): Promise<AxiosResponse<Appeal[]>> {
    const requestParams = new URLSearchParams();
    if (author !== undefined) {
      requestParams.append('author', String(author));
    }
    return $api.get<Appeal[]>(
      `/appeals` + (requestParams ? `?${requestParams}` : ``)
    );
  }

  static async getAppealsListByRole(
    id?: number
  ): Promise<AxiosResponse<Appeal[]>> {
    const requestParams = new URLSearchParams();
    if (id !== undefined) {
      requestParams.append('id', String(id));
    }

    return $api.get<Appeal[]>(
      `/appeals/by_role` + (requestParams ? `?${requestParams}` : ``)
    );
  }

  static async getCoordinators(): Promise<
    AxiosResponse<MunicipalityExecutors>
  > {
    return $api.get<MunicipalityExecutors>(`appeals/coordinator_selects`);
  }

  static async getAppealAnswers(
    appealId?: number
  ): Promise<AxiosResponse<AppealAnswer[]>> {
    const requestParams = new URLSearchParams();
    if (appealId !== undefined) {
      requestParams.append('appeal', String(appealId));
    }
    return $api.get<AppealAnswer[]>(
      `/appeal_answers` + (requestParams ? `?${requestParams}` : ``)
    );
  }

  static async getAppeal(
    appealId: number
  ): Promise<AxiosResponse<CitizenMessage>> {
    return $api.get<CitizenMessage>(`/appeal/${appealId}`);
  }

  static async getAppealAnswer(
    id: number
  ): Promise<AxiosResponse<AppealAnswer>> {
    return $api.get<AppealAnswer>(`/appeal_answers/${id}`);
  }

  static async getAppealSolvedAnswer(
    id: number
  ): Promise<AxiosResponse<AppealAnswer[]>> {
    return $api.get<AppealAnswer[]>(`/appeal/${id}/solved_answer`);
  }

  static async getAppealStatuses(
    isUser?: boolean
  ): Promise<AxiosResponse<Status[]>> {
    return $api.get<Status[]>(
      `/appeal/statuses` + (isUser ? `?status=user` : ``)
    );
  }

  static async getPlacemarks(): Promise<AxiosResponse<AppealPlacemark[]>> {
    return $api.get<AppealPlacemark[]>('/placemarks');
  }

  static async getPage(id: number): Promise<AxiosResponse<Page>> {
    return $api.get<Page>(`/pages/${id}`);
  }

  static async getCurrentUserInfo(
    userId: number
  ): Promise<AxiosResponse<User>> {
    return $api.get<User>(`/users/${userId}`);
  }

  static async setAppeal(
    appeal: AppealForm
  ): Promise<AxiosResponse<AppealForm>> {
    return $api.post<AppealForm>('/appeals', appeal);
  }

  static async setAppealModerationStatus(
    appealStatus: AppealStatus
  ): Promise<AxiosResponse<AppealStatus>> {
    const moderationStatus = {
      moderatorComment: appealStatus.comment,
      rejectionReason: appealStatus.rejectionReason
        ? appealStatus.rejectionReason
        : null,
      accepted: appealStatus.status,
    };
    return $api.put<AppealStatus>(
      `/appeals/${appealStatus.id}/moderation`,
      moderationStatus
    );
  }

  static async setAppealCoordination(
    appealId: number,
    coordinationExecutors: AppealCoordination
  ): Promise<AxiosResponse<AppealStatus>> {
    return $api.put<AppealStatus>(
      `/appeals/${appealId}/coordination`,
      coordinationExecutors
    );
  }

  static async setAppealApproval(
    appealAnswerId: number,
    appealApproval: AppealApproval
  ): Promise<AxiosResponse<AppealStatus>> {
    return $api.put<AppealStatus>(
      `/appeal_resolves/${appealAnswerId}`,
      appealApproval
    );
  }

  static async setAppealAnswer(
    executorAnswer: ExecutorAnswer
  ): Promise<AxiosResponse<ExecutorAnswer>> {
    return $api.post<ExecutorAnswer>(`/appeal_answers`, executorAnswer);
  }

  static async setAppealAccept(
    appealId: number
  ): Promise<AxiosResponse<AppealAccept>> {
    return $api.put<AppealAccept>(`/appeals/${appealId}/accept`, {
      isAccepted: true,
    });
  }
}
